import { useLayoutEffect, useRef } from 'react'
import { twMerge } from 'tailwind-merge'
import { useIsDark } from '@highlight/ui'
import { BlockStream } from './block-stream'

interface BlockStreamBarProps {
  /** Real progress, 0..1. A jump streams blocks in, faster and denser the bigger it is. */
  progress: number
  /** Milliseconds the bar keeps auto-filling after a step lands. */
  timespan?: number
  className?: string
}

const DEFAULT_TIMESPAN_MS = 8000

// The card unmounts when minimized and mounts again on expand. The displayed
// fill outlives it so the auto-fill doesn't snap back to the real value each time.
// One bar at a time, so a module-level value is enough.
let carriedFraction: number | null = null

/**
 * The learning card's progress bar. Real progress arrives in a few big steps
 * (one per integration), so between steps the bar auto-fills slowly, and when
 * a step lands the remainder streams in from the right as 2×2 blocks.
 */
export function BlockStreamBar({ progress, timespan = DEFAULT_TIMESPAN_MS, className }: BlockStreamBarProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const inputRef = useRef({ value: progress * 100, timespan })
  // The theme class flips in another component's effect, so re-read the color on the next frame rather than now.
  const colorStaleRef = useRef(true)
  const isDark = useIsDark()

  useLayoutEffect(() => {
    inputRef.current = { value: progress * 100, timespan }
  }, [progress, timespan])

  useLayoutEffect(() => {
    colorStaleRef.current = true
  }, [isDark])

  useLayoutEffect(() => {
    const canvas = canvasRef.current
    const host = hostRef.current
    if (!canvas || !host) return

    const stream = new BlockStream(canvas)
    let frame = 0

    const loop = (now: number) => {
      const input = inputRef.current
      if (colorStaleRef.current) {
        colorStaleRef.current = false
        stream.setColor(getComputedStyle(canvas).color)
      }
      stream.setInputs(input.value, input.timespan)
      stream.frame(now)
      frame = requestAnimationFrame(loop)
    }

    stream.setInputs(inputRef.current.value, inputRef.current.timespan)
    stream.resize(host.clientWidth)
    if (carriedFraction !== null) stream.restore(carriedFraction)
    frame = requestAnimationFrame(loop)

    const observer = new ResizeObserver(() => stream.resize(host.clientWidth))
    observer.observe(host)

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      carriedFraction = stream.fraction()
    }
  }, [])

  return (
    <div
      ref={hostRef}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress * 100)}
      aria-label="Context generation progress"
      className={twMerge('bg-element relative h-1 w-full overflow-hidden', className)}
    >
      <canvas
        ref={canvasRef}
        aria-hidden
        className="absolute inset-0 h-full w-full text-[color:var(--info)]"
        style={{ imageRendering: 'pixelated' }}
      />
    </div>
  )
}
